import React from "react";

import type { UseMutationResult } from "@tanstack/react-query";

import Button from "@mui/material/Button";

import Modal from "@app/analytics/components/Modal";
import ManageTags from "@app/analytics/components/TagPopover/ManageTags";
import ManageTagsPopover, {
  ManageTagsPopoverProps,
} from "@app/analytics/components/TagPopover/ManageTagsPopover";
import useManageTags, {
  UseManageTagsArgs,
} from "@app/analytics/components/TagPopover/useManageTags";
import useReportsTable from "@app/analytics/MeetingReports/stores/reportsTable";
import useSessionTags from "@app/common/hooks/tags/useSessionTags";
import useTags from "@app/common/hooks/tags/useTags";
import type { Tag, Tag as ITag, UserMeeting } from "@app/common/types";

import { ModalTitle } from "@rui/Modal";
import { Column, Row } from "@rui/Stack";

const EMPTY_TAGS: ITag[] = [];

interface ManageReportTagsProps {
  allTags: Tag[];
  report: UserMeeting;
  onSave: UseMutationResult<
    void,
    unknown,
    { updatedTags: Tag[]; applyToSeries?: boolean },
    unknown
  >;
}

type ManageReportTagsPopoverProps = ManageReportTagsProps & {
  popoverProps: ManageTagsPopoverProps["popoverProps"];
};

const ManageReportTagsDialog = ({
  report,
  allTags,
  onSave,
}: ManageReportTagsProps) => {
  const [open, closeDialog] = useReportsTable((state) => [
    state.dialog?.id === "tags" && state.dialog?.reportId === report.id,
    state.closeDialog,
  ]);

  const handleSaveTags: UseManageTagsArgs["onSave"] = (
    updatedTags,
    applyToSeries
  ) => {
    onSave.mutate(
      { updatedTags, applyToSeries },
      {
        onSuccess: closeDialog,
      }
    );
  };

  const canApplyToSeries = Boolean(report.seriesId);

  const { onRemoveAll, ...manageTagsProps } = useManageTags({
    allTags,
    appliedTags: report.tags ?? [],
    canApplyToSeries,
    onSave: handleSaveTags,
  });

  return (
    <Modal
      open={open}
      disableAutoFocus
      onClose={closeDialog}
      sx={{ width: "360px" }}
    >
      <Column py={2} px={3} spacing={2}>
        <Row variant="apart" spacing={1}>
          <ModalTitle>Tags ({report.tags?.length ?? 0})</ModalTitle>
          <Button
            color="error"
            sx={{ fontSize: "0.75rem" }}
            onClick={onRemoveAll}
            disabled={manageTagsProps.appliedTags.length < 1}
          >
            Remove all
          </Button>
        </Row>
        <Column spacing={1}>
          <ManageTags
            {...manageTagsProps}
            canApplyToSeries={canApplyToSeries}
            loading={onSave.isPending}
            onClose={closeDialog}
          />
        </Column>
      </Column>
    </Modal>
  );
};

const ManageReportTagsPopover = ({
  allTags,
  report,
  onSave,
  popoverProps,
}: ManageReportTagsPopoverProps) => {
  const handleSaveTags = (updatedTags: ITag[], applyToSeries?: boolean) => {
    onSave.mutate({ updatedTags, applyToSeries });
    popoverProps?.onClose();
  };

  return (
    <ManageTagsPopover
      popoverProps={popoverProps}
      allTags={allTags}
      appliedTags={report.tags ?? EMPTY_TAGS}
      canApplyToSeries={Boolean(report.seriesId)}
      onSave={handleSaveTags}
    />
  );
};

const ManageMeetingReportTags = ({
  report,
  variant,
  popoverProps,
}: {
  report: UserMeeting;
  variant: "popover" | "dialog";
  popoverProps?: ManageTagsPopoverProps["popoverProps"];
}) => {
  const { tags: allUserTags } = useTags();

  const { updateSessionTags } = useSessionTags({
    sessionId: report.id,
    sessionTags: report.tags ?? EMPTY_TAGS,
    context: "sessionsList",
  });

  if (variant === "dialog") {
    return (
      <ManageReportTagsDialog
        allTags={allUserTags}
        report={report}
        onSave={updateSessionTags}
      />
    );
  }

  return (
    <ManageReportTagsPopover
      allTags={allUserTags}
      report={report}
      onSave={updateSessionTags}
      popoverProps={popoverProps}
    />
  );
};

export default ManageMeetingReportTags;
